import { TableProps } from 'antd'
import type { SortOrder } from 'antd/es/table/interface'
import formatAmount from 'shared/helpers/formatAmount'
import formatDate from 'shared/helpers/formatDate'
import getCustomerAddress from 'shared/helpers/getCustomerAddress'
import getCustomerName from 'shared/helpers/getCustomerName'
import { Customer, Invoice } from 'types'
import { SORT_PREFIX } from './constants'
import { EMPTY_CELL_VALUE } from 'constants/display'
import { FilterConfig } from './types'
import {
  sumLinesVat,
  sumLinesInclTax,
} from 'features/invoices/helpers/invoiceCalculations'
import {
  DateRangeFilterDropdown,
  NumberFilterDropdown,
  SearchFilterDropdown,
  StatusFilterDropdown,
} from './FilterDropdowns'
import InvoiceStatusTags from 'features/invoices/components/InvoiceStatusTags'

const getSortOrder = (sort: string | undefined, field: string): SortOrder => {
  if (sort === `${SORT_PREFIX.ASC}${field}`) return 'ascend'
  if (sort === `${SORT_PREFIX.DESC}${field}`) return 'descend'
  return null
}

export const tableColumns = (
  config: FilterConfig
): TableProps<Invoice>['columns'] => [
  {
    key: 'id',
    title: 'Id',
    dataIndex: 'id',
    filtered: !!config.searchId,
    filterDropdown: () => (
      <NumberFilterDropdown
        value={config.searchId}
        onChange={config.onIdSearchChange}
        placeholder="Invoice ID"
        min={1}
      />
    ),
  },
  {
    key: 'customer',
    title: 'Customer',
    dataIndex: 'customer',
    render: (customer: Customer) =>
      getCustomerName(customer) ?? EMPTY_CELL_VALUE,
    filtered: !!config.searchCustomer,
    filterDropdown: () => (
      <SearchFilterDropdown
        value={config.searchCustomer}
        onChange={config.onCustomerSearchChange}
        placeholder="Customer name"
      />
    ),
  },
  {
    key: 'address',
    title: 'Address',
    dataIndex: 'customer',
    render: (customer: Customer) =>
      getCustomerAddress(customer) ?? EMPTY_CELL_VALUE,
    filtered: !!config.searchAddress,
    filterDropdown: () => (
      <SearchFilterDropdown
        value={config.searchAddress}
        onChange={config.onAddressSearchChange}
        placeholder="Address, city or zip"
      />
    ),
  },
  {
    key: 'date',
    title: 'Date',
    dataIndex: 'date',
    render: (date: string) => (date ? formatDate(date) : EMPTY_CELL_VALUE),
    sorter: true,
    sortOrder: getSortOrder(config.sort, 'date'),
    filtered: !!(config.dateFrom || config.dateTo),
    filterDropdown: () => (
      <DateRangeFilterDropdown
        from={config.dateFrom}
        to={config.dateTo}
        onChange={config.onDateRangeChange}
        placeholder={['From date', 'To date']}
      />
    ),
  },
  {
    key: 'deadline',
    title: 'Deadline',
    dataIndex: 'deadline',
    render: (deadline: string) =>
      deadline ? formatDate(deadline) : EMPTY_CELL_VALUE,
    sorter: true,
    sortOrder: getSortOrder(config.sort, 'deadline'),
    filtered: !!(config.deadlineFrom || config.deadlineTo),
    filterDropdown: () => (
      <DateRangeFilterDropdown
        from={config.deadlineFrom}
        to={config.deadlineTo}
        onChange={config.onDeadlineRangeChange}
        placeholder={['From deadline', 'To deadline']}
      />
    ),
  },
  {
    key: 'tax',
    title: 'Tax',
    render: (_: unknown, record: Invoice) =>
      formatAmount(sumLinesVat(record.invoice_lines)),
  },
  {
    key: 'total',
    title: 'Total',
    render: (_: unknown, record: Invoice) =>
      formatAmount(sumLinesInclTax(record.invoice_lines)),
    sorter: true,
    sortOrder: getSortOrder(config.sort, 'total'),
  },
  {
    key: 'status',
    title: 'Status',
    render: (_: unknown, record: Invoice) => (
      <InvoiceStatusTags invoice={record} />
    ),
    filtered: config.statusFilter.length > 0,
    filterDropdown: () => (
      <StatusFilterDropdown
        value={config.statusFilter}
        onChange={config.onStatusFilterChange}
      />
    ),
  },
]
